import { useParams } from "react-router-dom";
import useFetch from "../../../../hooks/useFetch.js";
import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Collapse, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import { getV2CompetitionDetails, getV2SportEvents, postV2Phase, postV2Epreuve, postV2SportEventAsOne, deleteSportEvent } from "../../../../../api/index.js";
import CompetitionDTO from "../../../../DTO/CompetitionDTO.js";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { getUserProvider } from "../../../../../actions/auth.js";
import PhaseDTO from "../../../../DTO/PhaseDTO.js";
import EpreuveDTO from "../../../../DTO/EpreuveDTO.js";
import { useDebounce } from "../../../../hooks/useDebounce.js";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from "date-fns/locale";
import useDelete from "../../../../hooks/useDelete.js";
import { useNotifications } from "../../../../notifications/NotificationManager.js";

const CompetitionResolver = () => {
  const { idCompetition } = useParams();
  const [competition, setCompetition] = useState();
  const [epreuveToUpdate, setEpreuveToUpdate] = useState();
  const [phaseToUpdate, setPhaseToUpdate] = useState();
  const [sportEventToUpdate, setSportEventToUpdate] = useState();
  const [isFetchingSportEvents, setIsFetchingSportEvents] = useState(false);

  const { data, isLoading, error, fetchData } = useFetch(getV2CompetitionDetails, [idCompetition]);
  const { data: epreuveData, error: epreuveError, fetchData: epreuveFetchData } = useFetch(postV2Epreuve, [[epreuveToUpdate]]);
  const { data: phaseData, error: phaseError, fetchData: phaseFetchData } = useFetch(postV2Phase, [[phaseToUpdate]]);
  const { data: sportEventData, error: sportEventError, fetchData: sportEventFetchData } = useFetch(postV2SportEventAsOne, [sportEventToUpdate]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!data) return;
    setCompetition(new CompetitionDTO(data));
  }, [data]);

  useEffect(() => {
    if (epreuveToUpdate) epreuveFetchData();
  }, [epreuveToUpdate]);

  useEffect(() => {
    if (phaseToUpdate) phaseFetchData();
  }, [phaseToUpdate]);

  useEffect(() => {
    if (sportEventToUpdate) {
      sportEventFetchData().then(() => {
        setSportEventToUpdate(null);
        setIsFetchingSportEvents(true); // Déclencher la récupération des événements sportifs
      });
    }
  }, [sportEventToUpdate]);

  useEffect(() => {
    if (epreuveData) {
      setCompetition(prevCompetition => {
        const updatedCompetition = { ...prevCompetition };
        updatedCompetition.competitionsDisciplines.forEach(discipline => {
          discipline.phases.forEach(phase => {
            if (phase.epreuve._id === epreuveData._id) {
              phase.epreuve.name = epreuveData.name;
            }
          });
        });
        return updatedCompetition;
      });
    }
  }, [epreuveData]);

  if (isLoading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Typography variant="h6" color="error">Erreur lors du chargement des données : {error.message}</Typography>;
  }

  if (!competition) {
    return <Typography variant="h6">Aucune donnée de compétition disponible</Typography>;
  }

  return (
    <Box sx={{ textAlign: 'center', backgroundColor: '#80808045' }}>
      <Typography variant="h6" sx={{ fontSize: '1.1rem', padding: '0.5em', width: '100%' }}>{data.name}</Typography>
      <CompetitionTable 
        data={competition} 
        setEpreuveToUpdate={setEpreuveToUpdate} 
        setPhaseToUpdate={setPhaseToUpdate} 
        setSportEventToUpdate={setSportEventToUpdate} 
        isFetchingSportEvents={isFetchingSportEvents} 
        setIsFetchingSportEvents={setIsFetchingSportEvents}
        competitionId={idCompetition}
      />
    </Box>
  );
};

const SportEventRow = ({ sportEvent, setSportEventToUpdate, isNew = false, onCancel, onRemove }) => {
  const [localSportEvent, setLocalSportEvent] = useState(sportEvent);

  const handleSportEventEdit = useCallback((newValue, field) => {
    setLocalSportEvent(prevState => {
      const updatedSportEvent = { ...prevState, [field]: newValue, provider: getUserProvider() };
      setSportEventToUpdate(updatedSportEvent);  // Trigger update immediately
      //setIsN
      return updatedSportEvent;
    });
  }, [setSportEventToUpdate]);

  const { value: nameValue, setValue: setNameValue, handleChangeWithField: handleNameChange } = useDebounce(handleSportEventEdit);
  const { value: dateValue, setValue: setDateValue, handleChangeWithField: handleDateChange } = useDebounce(handleSportEventEdit);

  useEffect(() => {
    setNameValue(localSportEvent.name || "");
    setDateValue(localSportEvent.date || null);
  }, [localSportEvent, setNameValue, setDateValue]);

  useEffect(() => {
    setLocalSportEvent(sportEvent);
  }, [sportEvent]);

  return (
    <TableRow sx={{ display: 'flex' }}>
      {/*SportEvent upsert*/}
      <TableCell>
        <TextField  
          sx={{ marginLeft: '10em', minWidth: '30em' }} 
          label="Sport Event" 
          value={nameValue} 
          onChange={(e) => handleNameChange(e.target.value, "name")} 
        />
      </TableCell>
      <TableCell>
        <LocalizationProvider dateAdapter={AdapterDateFns} locale={fr}>
          <DateTimePicker
            label="Date"
            value={dateValue}
            onChange={(newValue) => handleDateChange(newValue, "date")}
            renderInput={(props) => <TextField {...props} />}
            inputFormat="dd/MM/yyyy HH:mm"
          />
        </LocalizationProvider>
      </TableCell>
      {/*SportEvent deletion specific*/}
      {!isNew &&  
      <>
        <TableCell>
          <Button variant="contained" sx={{marginTop: '5px'}} onClick={() => onRemove(localSportEvent._id)}><ClearIcon fontSize="small"/></Button>
        </TableCell>
      </>
        }
      {/*SportEvent creation specific */}
      {isNew && (
        <>
          <TableCell>
            <Button variant="contained" sx={{marginTop: '5px'}} onClick={onCancel}>Annuler</Button>
          </TableCell>
        </>
      )}
    </TableRow>
  );
};

const PhaseRow = ({ row, setPhaseToUpdate, setSportEventToUpdate, epreuveId, categoryId, competitionId, sportIdName, isFetchingSportEvents, setIsFetchingSportEvents }) => {
  const { addNotification } = useNotifications();
  const [open, setOpen] = useState(false);
  const [sportEvents, setSportEvents] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [sportEventToDeleteId, setSportEventToDeleteId] = useState(undefined);

  const { isDeleting, error: deleteError, isDeleted, deleteData, setIsDeleted } = useDelete(deleteSportEvent, sportEventToDeleteId);

  const fetchSportEvents = useCallback(() => {
    getV2SportEvents({ global: { phase: row._id } }).then(response => {
      setSportEvents(response.data);
      setIsFetchingSportEvents(false);
    });
  }, [row._id, setIsFetchingSportEvents]);

  const handlePhaseEdit = useCallback((value) => {
    const phaseData = {
      _id: row._id,
      name: value,
      provider: getUserProvider()
    };
    setPhaseToUpdate(new PhaseDTO(phaseData));
  }, [row._id, setPhaseToUpdate]);

  const { value: phaseValue, setValue: setPhaseValue, handleChange: handlePhaseChange } = useDebounce(handlePhaseEdit);

  useEffect(() => {
    setPhaseValue(row.name);
  }, [row.name, setPhaseValue]);

  useEffect(() => {
    if (open) {
      fetchSportEvents();
    }
  }, [open, fetchSportEvents]);

  useEffect(() => {
    if (isFetchingSportEvents) {
      fetchSportEvents();
    }
  }, [isFetchingSportEvents, fetchSportEvents]);

  const handleAddSportEvent = () => {
    setIsAdding(true);
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
  };

  useEffect(()=>{
    handleCancelAdd();
  },[sportEvents])

  const handleRemove = (sportEventId) => {
    setSportEventToDeleteId(sportEventId)
  }

  useEffect(()=>{
    if (sportEventToDeleteId)
        deleteData();
  },[sportEventToDeleteId])

      //post-remove
      useEffect(() => {
        if (isDeleted) {
           let updatedSportEvents = sportEvents.filter(spe => spe._id !== sportEventToDeleteId)
          setSportEvents(updatedSportEvents);
           setIsDeleted(false)
        }
      }, [setIsDeleted, sportEventToDeleteId, sportEvents, isDeleted]);


      //Handle SportEvent deletion error
      useEffect(() => {
        if (deleteError) {
      
          const errorStatus = deleteError?.status ? deleteError.status : 'Unknown Status';
          const errorMessage = deleteError?.message ? deleteError.message : 'Unknown Error';
      
          addNotification({
            title: 'Suppression d\'un évènement',
            type: 'error',
            description: `${errorStatus} - ${errorMessage}`,
          });
        }
      }, [addNotification, deleteError]);



  return (
    <React.Fragment>
      <TableRow>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
          <TextField sx={{ marginLeft: '5em', minWidth: '30em' }} label="Phase" value={phaseValue} onChange={(e) => handlePhaseChange(e.target.value)} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Table size="small" aria-label="sport-events">
                <TableBody>
                  {sportEvents.map((sportEvent) => (
                    <SportEventRow key={sportEvent._id} sportEvent={sportEvent} setSportEventToUpdate={setSportEventToUpdate} onRemove={handleRemove} />
                  ))}
                  {isAdding && (
                    <SportEventRow 
                      sportEvent={{
                        ...sportEvents[0], 
                        _id: undefined, 
                        name: 'Nouvel évènement', 
                        date: new Date(),
                        category: categoryId,
                        epreuve: epreuveId,
                        phase: row._id,
                        competition: competitionId,
                        mainSport: sportIdName
                      }} 
                      setSportEventToUpdate={setSportEventToUpdate} 
                      isNew={true}
                      onCancel={handleCancelAdd}
                    />
                  )}
                </TableBody>
              </Table>
              {!isAdding && (
                <Button variant="contained" sx={{marginLeft: '12.5em', marginTop: '5px'}} onClick={handleAddSportEvent}>+</Button>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const Row = ({ index, row, setEpreuveToUpdate, setPhaseToUpdate, setSportEventToUpdate, isFetchingSportEvents, setIsFetchingSportEvents,  competitionId, sportIdName }) => {
  const [open, setOpen] = useState(false);

  const handleEpreuveEdit = useCallback((value) => {
    const epreuveData = {
      _id: row.epreuve_id,
      name: value,
      provider: getUserProvider()
    };
    setEpreuveToUpdate(new EpreuveDTO(epreuveData));
  }, [row.epreuve_id, setEpreuveToUpdate]);

  const { value: epreuveValue, setValue: setEpreuveValue, handleChange: handleEpreuveChange } = useDebounce(handleEpreuveEdit);

  useEffect(() => {
    setEpreuveValue(row.epreuve_name);
  }, [row.epreuve_name, setEpreuveValue]);

  return (
    <React.Fragment>
      <TableRow sx={{ backgroundColor: index % 2 === 0 ? '#d8d8d882' : 'white' }}>
        <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
          <TextField sx={{ marginLeft: '1em', minWidth: '30em' }} label='Epreuve' value={epreuveValue} onChange={(e) => handleEpreuveChange(e.target.value)} />
          <Typography sx={{ marginLeft: '4em' }} label='Categorie' > {row.category_name}</Typography>
        </TableCell>
        <TableCell></TableCell>
      </TableRow>
      <TableRow sx={{ backgroundColor: index % 2 === 0 ? '#d8d8d882' : 'white' }}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Table size="small" aria-label="phases">
                <TableBody>
                  {row.phases.map((phase, index) => (
                    <PhaseRow 
                      key={index} 
                      row={phase} 
                      setPhaseToUpdate={setPhaseToUpdate} 
                      setSportEventToUpdate={setSportEventToUpdate} 
                      epreuveId={row.epreuve_id} 
                      categoryId={row.category_id}
                      competitionId={competitionId}
                      sportIdName={sportIdName}
                      isFetchingSportEvents={isFetchingSportEvents} 
                      setIsFetchingSportEvents={setIsFetchingSportEvents}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const CompetitionTable = ({ data, setEpreuveToUpdate, setPhaseToUpdate, setSportEventToUpdate, isFetchingSportEvents, setIsFetchingSportEvents,  competitionId}) => {
  const [openDiscipline, setOpenDiscipline] = useState({});

  const handleToggleDiscipline = (index) => {
    setOpenDiscipline(prevState => ({
      ...prevState,
      [index]: !prevState[index]
    }));
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.competitionsDisciplines.sort((a, b) => a.discipline?.sport?.label?.localeCompare(b.discipline?.sport?.label)).map((discipline, index) => {
            const rows = discipline.phases.reduce((acc, phase) => {
              const { epreuve, category } = phase;
              const existing = acc.find(
                item =>
                  item.epreuve_id === epreuve._id && item.category_id === category._id
              );
              if (existing) {
                existing.phases.push(phase);
              } else {
                acc.push({
                  epreuve_id: epreuve._id,
                  epreuve_name: epreuve.name,
                  category_id: category._id,
                  category_name: category.name,
                  phases: [phase]
                });
              }
              return acc;
            }, []);

            return (
              <React.Fragment key={index}>
                <TableRow>
                  <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => handleToggleDiscipline(index)}
                    >
                      {openDiscipline[index] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', paddingLeft: '0.5em' }}>
                      {discipline.discipline.sport.label} - {discipline.discipline.label}
                    </Typography>
                  </TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                    <Collapse in={openDiscipline[index]} timeout="auto" unmountOnExit>
                      <Box margin={1}>
                        <Table size="small" aria-label="rows">
                          <TableBody>
                            {rows.map((row, rowIndex) => (
                              <Row 
                                key={rowIndex} 
                                index={rowIndex} 
                                row={row} 
                                setEpreuveToUpdate={setEpreuveToUpdate} 
                                setPhaseToUpdate={setPhaseToUpdate} 
                                setSportEventToUpdate={setSportEventToUpdate}
                                isFetchingSportEvents={isFetchingSportEvents} 
                                setIsFetchingSportEvents={setIsFetchingSportEvents}
                                competitionId={competitionId}
                                sportIdName={discipline.discipline.sport.idName}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CompetitionResolver;